// Shared reactive peaks state used by both the main waveform and the minimap.
// Loading is driven from `TimelineWaveform.svelte` (or anywhere else that
// owns the media context); both consumers read the resolved peaks from here.

import { loadPeaks, FLAT_FALLBACK_PEAKS } from "./peaks.js";

export const peaksState = $state({
    data: FLAT_FALLBACK_PEAKS,
    loading: false,
    signature: ""
});

// Monotonic token identifying the most recent refresh request. Async loads
// only publish their result if they are still the latest request, so an older
// (slower) load can never clobber the peaks of the media the user switched to.
let currentRequestId = 0;

function signatureFor(media) {
    if (!media) return "";
    return [
        media.peaksPath || "",
        Array.isArray(media.peaksData) ? `inline:${media.peaksData.length}` : "",
        media.path || media.url || media.localPath || "",
        media.storage || "",
        media.duration || media?.info?.duration || ""
    ].join("|");
}

/**
 * Publish flat fallback peaks while waiting for media duration (before Web Audio
 * extraction can safely start). Updates signature so consumers invalidate tiles.
 *
 * @param {object} media
 */
export function setPendingPeaks(media) {
    currentRequestId += 1;
    peaksState.data = FLAT_FALLBACK_PEAKS;
    peaksState.loading = false;
    peaksState.signature = `${signatureFor(media)}|pending-duration`;
}

/**
 * Load peaks for the supplied media and publish them to `peaksState`. Idempotent
 * across identical media signatures; concurrent calls are resolved last-wins.
 *
 * @param {object} media
 * @param {number} [durationSec]
 */
export async function refreshPeaks(media, durationSec = 0) {
    if (!media) {
        currentRequestId += 1;
        peaksState.data = FLAT_FALLBACK_PEAKS;
        peaksState.loading = false;
        peaksState.signature = "";
        return;
    }
    const sig = signatureFor(media);
    if (sig === peaksState.signature && !peaksState.loading) return;

    const requestId = ++currentRequestId;
    peaksState.signature = sig;
    peaksState.loading = true;
    try {
        const result = await loadPeaks(media, { durationSec });
        if (requestId !== currentRequestId) return;
        peaksState.data = result || FLAT_FALLBACK_PEAKS;
    } catch (err) {
        if (requestId !== currentRequestId) return;
        // eslint-disable-next-line no-console
        console.warn("[timeline.peaksState] load failed:", err?.message);
        peaksState.data = FLAT_FALLBACK_PEAKS;
    } finally {
        if (requestId === currentRequestId) {
            peaksState.loading = false;
        }
    }
}

/**
 * Invalidate the cached signature (without flashing the displayed data) so the
 * next `refreshPeaks` call always reloads, even for an identical signature.
 * Call when the active media version changes or peaks were regenerated.
 */
export function invalidatePeaks() {
    currentRequestId += 1;
    peaksState.signature = "";
    peaksState.loading = false;
}

/**
 * Reset peaks state (call on full project close).
 */
export function resetPeaksState() {
    currentRequestId += 1;
    peaksState.data = FLAT_FALLBACK_PEAKS;
    peaksState.loading = false;
    peaksState.signature = "";
}
